import { useState } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const MOIS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const pad = (n) => String(n).padStart(2, "0");
const lire = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v || "");
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : undefined;
};
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const isoMois = (d) => iso(d).slice(0, 7);

// Ce mois (1er → dernier jour), mois dernier, cette année (1er janvier → aujourd'hui)
export const periodesRapides = (auj = new Date()) => {
  const y = auj.getFullYear(), m = auj.getMonth();
  return [
    { id: "ce-mois", label: "Ce mois", debut: new Date(y, m, 1), fin: new Date(y, m + 1, 0) },
    { id: "mois-dernier", label: "Mois dernier", debut: new Date(y, m - 1, 1), fin: new Date(y, m, 0) },
    { id: "cette-annee", label: "Cette année", debut: new Date(y, 0, 1), fin: new Date(y, m, auj.getDate()) },
  ];
};

const Raccourcis = ({ onChoix, testId }) => (
  <div className="flex gap-1 border-b p-2" data-testid={testId && `${testId}-raccourcis`}>
    {periodesRapides().map((p) => (
      <Button key={p.id} type="button" size="sm" variant="ghost" className="h-7 flex-1 px-2 text-xs text-emerald-800 hover:bg-emerald-50"
        onClick={() => onChoix(p)} data-testid={testId && `${testId}-raccourci-${p.id}`}>{p.label}</Button>
    ))}
  </div>
);

const Declencheur = ({ texte, vide, className, testId, ...props }) => (
  <Button type="button" variant="outline" {...props} data-testid={testId}
    className={cn("h-9 justify-start px-3 font-normal tabular", vide && "text-slate-400", className)}>
    <CalendarDays className="mr-2 h-3.5 w-3.5 shrink-0 text-slate-400" /><span className="truncate">{texte}</span>
  </Button>
);

const Requis = ({ value }) => (
  <input tabIndex={-1} required value={value || ""} onChange={() => {}} aria-hidden="true"
    className="pointer-events-none absolute h-px w-px opacity-0" />
);

// onPeriode({debut, fin}) : champ d'une période → les raccourcis remplissent les deux bornes ; sinon ils affichent le mois
export const DateField = ({ value, onChange, onPeriode, className, required, placeholder = "Choisir", "data-testid": testId }) => {
  const [ouvert, setOuvert] = useState(false);
  const d = lire(value);
  const [vue, setVue] = useState(d);
  const ouvrir = (o) => { if (o) setVue(d || new Date()); setOuvert(o); };
  const raccourci = (p) => {
    if (onPeriode) { onPeriode({ debut: iso(p.debut), fin: iso(p.fin) }); setOuvert(false); } else setVue(p.debut);
  };
  return (
    <>
      <Popover open={ouvert} onOpenChange={ouvrir}>
        <PopoverTrigger asChild>
          <Declencheur texte={d ? format(d, "d MMM yyyy", { locale: fr }) : placeholder} vide={!d} className={className} testId={testId} />
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start" data-testid={testId && `${testId}-popover`}>
          <Raccourcis onChoix={raccourci} testId={testId} />
          <Calendar mode="single" locale={fr} weekStartsOn={1} selected={d} month={vue} onMonthChange={setVue} initialFocus
            onSelect={(x) => { if (x) { onChange(iso(x)); setOuvert(false); } }} />
          {!required && d && (
            <button type="button" className="w-full border-t py-2 text-xs text-slate-500 hover:text-red-600" data-testid={testId && `${testId}-effacer`}
              onClick={() => { onChange(""); setOuvert(false); }}>Effacer la date</button>
          )}
        </PopoverContent>
      </Popover>
      {required && <Requis value={value} />}
    </>
  );
};

const GrilleMois = ({ annee, setAnnee, a, m, choisir, testId }) => (
  <div className="p-3">
    <div className="mb-2 flex items-center justify-between">
      <Button type="button" variant="outline" size="icon" className="h-7 w-7" onClick={() => setAnnee(annee - 1)} data-testid={testId && `${testId}-annee-prec`}><ChevronLeft className="h-4 w-4" /></Button>
      <span className="text-sm font-medium tabular" data-testid={testId && `${testId}-annee`}>{annee}</span>
      <Button type="button" variant="outline" size="icon" className="h-7 w-7" onClick={() => setAnnee(annee + 1)} data-testid={testId && `${testId}-annee-suiv`}><ChevronRight className="h-4 w-4" /></Button>
    </div>
    <div className="grid grid-cols-3 gap-1">
      {MOIS.map((nom, i) => {
        const actif = annee === a && i + 1 === m;
        return (
          <Button key={nom} type="button" size="sm" variant={actif ? "default" : "ghost"} data-testid={testId && `${testId}-mois-${pad(i + 1)}`}
            className={cn("h-8 text-xs", actif && "bg-emerald-700 hover:bg-emerald-800")} onClick={() => choisir(`${annee}-${pad(i + 1)}`)}>{nom}</Button>
        );
      })}
    </div>
  </div>
);

// onPeriode({debut, fin}) en AAAA-MM ; sans période : « Ce mois » / « Mois dernier » choisissent le mois, « Cette année » affiche l'année
export const MonthField = ({ value, onChange, onPeriode, className, placeholder = "Choisir", "data-testid": testId }) => {
  const [ouvert, setOuvert] = useState(false);
  const [a, m] = (value || "").split("-").map(Number);
  const [annee, setAnnee] = useState(a || new Date().getFullYear());
  const ouvrir = (o) => { if (o) setAnnee(a || new Date().getFullYear()); setOuvert(o); };
  const choisir = (v) => { onChange(v); setOuvert(false); };
  const raccourci = (p) => {
    if (onPeriode) { onPeriode({ debut: isoMois(p.debut), fin: isoMois(p.fin) }); setOuvert(false); }
    else if (p.id === "cette-annee") setAnnee(p.debut.getFullYear());
    else choisir(isoMois(p.debut));
  };
  return (
    <Popover open={ouvert} onOpenChange={ouvrir}>
      <PopoverTrigger asChild>
        <Declencheur texte={a && m ? `${MOIS[m - 1]} ${a}` : placeholder} vide={!a} className={className} testId={testId} />
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start" data-testid={testId && `${testId}-popover`}>
        <Raccourcis onChoix={raccourci} testId={testId} />
        <GrilleMois annee={annee} setAnnee={setAnnee} a={a} m={m} choisir={choisir} testId={testId} />
      </PopoverContent>
    </Popover>
  );
};
